/**
 * Style du fond de carte : volontairement muet.
 *
 * La carte n'est qu'une toile pour 816 marqueurs. Tout ce qui n'aide pas à les
 * situer — étiquettes de rues, points d'intérêt, relief, frontières
 * maritimes — est écarté : chaque élément dessiné est un élément qui rivalise
 * avec la donnée. Ne restent que les noms de lieux, l'eau et le tracé des
 * routes.
 */

import { STATUS_COLORS, STATUS_STROKES } from '../domain/constants.js'

const OFM = 'https://tiles.openfreemap.org'

/**
 * Palette des marqueurs, en expression MapLibre : la couleur suit le statut.
 * Construite depuis STATUS_COLORS pour qu'une seule définition existe.
 */
export const STATUS_COLOR = [
  'match', ['get', 'status'],
  'client', STATUS_COLORS.client,
  'gratuit', STATUS_COLORS.gratuit,
  'conversion', STATUS_COLORS.conversion,
  STATUS_COLORS.prospect, // et repli
]

/**
 * Contour : blanc par défaut, assombri là où le blanc ne détacherait rien du
 * fond clair de la carte — voir STATUS_STROKES.
 */
export const STROKE_COLOR = [
  'match', ['get', 'status'],
  'client', STATUS_STROKES.client,
  'gratuit', STATUS_STROKES.gratuit,
  'conversion', STATUS_STROKES.conversion,
  '#ffffff', // prospect : le rouge se détache seul
]

export const STROKE_WIDTH = ['interpolate', ['linear'], ['zoom'], 4, 0.8, 10, 1.4]

export const ATTRIBUTION = '© OpenStreetMap · OpenFreeMap · MSSS (RRSS)'

export const INITIAL_VIEW = {
  center: [-73.62, 45.53], // Montréal : la plus forte densité de GMF
  zoom: 9.5,
  minZoom: 4,
  maxZoom: 18,
}

export function baseStyle(sources) {
  return {
    version: 8,
    glyphs: `${OFM}/fonts/{fontstack}/{range}.pbf`,
    sources: {
      omt: { type: 'vector', url: `${OFM}/planet` },
      ...sources,
    },
    layers: [
      { id: 'bg', type: 'background', paint: { 'background-color': '#f5f5f3' } },
      {
        id: 'water',
        type: 'fill',
        source: 'omt',
        'source-layer': 'water',
        paint: { 'fill-color': '#e3e9ec' },
      },
      {
        id: 'waterway',
        type: 'line',
        source: 'omt',
        'source-layer': 'waterway',
        minzoom: 8,
        paint: {
          'line-color': '#d6dee2',
          'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.4, 14, 1.4],
        },
      },
      {
        id: 'boundary',
        type: 'line',
        source: 'omt',
        'source-layer': 'boundary',
        // Jusqu'au niveau 4 : pays et provinces. Les frontières maritimes sont
        // exclues, elles dessinent des traits en pleine mer.
        filter: ['all', ['<=', ['get', 'admin_level'], 4], ['!=', ['get', 'maritime'], 1]],
        paint: { 'line-color': '#dcdcd6', 'line-dasharray': [2.5, 2], 'line-width': 0.8 },
      },
      {
        id: 'road-minor',
        type: 'line',
        source: 'omt',
        'source-layer': 'transportation',
        minzoom: 11, // en deçà, la voirie de desserte n'est qu'un grisé
        filter: ['in', ['get', 'class'], ['literal',
          ['minor', 'service', 'residential', 'living_street', 'unclassified', 'tertiary', 'road']]],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#e3e3df',
          'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.4, 14, 1.1, 18, 4],
        },
      },
      {
        id: 'road-main',
        type: 'line',
        source: 'omt',
        'source-layer': 'transportation',
        filter: ['in', ['get', 'class'], ['literal', ['motorway', 'trunk', 'primary', 'secondary']]],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': ['match', ['get', 'class'], 'motorway', '#c6c6bf', 'trunk', '#cbcbc4', '#d3d3cd'],
          'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0.35, 8, 0.8, 11, 1.6, 14, 3.2, 18, 7],
        },
      },
      {
        id: 'place-city',
        type: 'symbol',
        source: 'omt',
        'source-layer': 'place',
        filter: ['in', ['get', 'class'], ['literal', ['city', 'town', 'village']]],
        layout: {
          'text-field': ['coalesce', ['get', 'name:fr'], ['get', 'name']],
          'text-font': ['Noto Sans Regular'],
          // La taille marque la hiérarchie ville / bourg / village, et les
          // villages n'apparaissent qu'en zoomant.
          'text-size': ['interpolate', ['linear'], ['zoom'],
            4, ['match', ['get', 'class'], 'city', 11, 'town', 9, 8],
            9, ['match', ['get', 'class'], 'city', 15, 'town', 12.5, 'village', 11, 10],
            13, ['match', ['get', 'class'], 'city', 18, 'town', 15, 'village', 13, 12]],
          'text-max-width': 7,
          'text-transform': ['match', ['get', 'class'], 'city', 'uppercase', 'none'],
          'text-letter-spacing': ['match', ['get', 'class'], 'city', 0.06, 0],
          'text-padding': 3,
        },
        paint: { 'text-color': '#43433d', 'text-halo-color': '#f5f5f3', 'text-halo-width': 1.3 },
      },
    ],
  }
}
